import docrot from 'assets/tools/docrot.png';
import hyperbench from 'assets/tools/hyperbench.png';
import labyrinth from 'assets/tools/labyrinth.png';
import nfctl from 'assets/tools/nfctl.png';
import oasis from 'assets/tools/oasis.png';
import orthodag from 'assets/tools/orthodag.png';
import rvu from 'assets/tools/rvu.png';
import space from 'assets/tools/space.png';
import totem from 'assets/tools/totem.png';
import wingman from 'assets/tools/wingman.png';

// Geometry is deliberately irregular: no two entries share a width, a vertical
// offset, or a resulting bottom edge. That withheld alignment is what makes the
// row read as suspended rather than as a grid. `lap` deepens left to right
// while z-index descends, so the leftmost sticker sits on top.
export const tools = [
  {
    id: 'labyrinth',
    mascot: { name: 'Labrys', text: 'is the Minotaur, who lives at the centre of every labyrinth. He is named for the labrys, the double axe of Crete that the word labyrinth is thought to come from.' },
    name: 'labyrinth',
    href: '/projects/labyrinth/',
    image: labyrinth,
    blurb:
      'I used to keep the whole lab in my head when deploying. lab.yml describes it, so agents can work across the lab at once.',
    meta: { Type: 'Framework', Language: 'OpenTofu / Ansible' },
    tags: ['homelab', 'proxmox', 'iac'],
    width: 236,
    offset: 30,
    lap: 0,
    z: 6,
    rot: 4,
  },
  {
    id: 'space',
    mascot: { name: 'Sol', text: 'is a ringed planet: a world in space, with its workspaces in orbit.' },
    name: 'space',
    href: '/projects/space/',
    image: space,
    blurb:
      'A ticket across a dozen services used to need a dozen checkouts. space puts them in one workspace on a shared branch, where agents can work in parallel.',
    meta: { Type: 'CLI', Language: 'Rust' },
    tags: ['worktrees', 'multi-repo', 'tui'],
    width: 196,
    offset: -28,
    lap: 70,
    z: 5,
    rot: -6,
  },
  {
    id: 'wingman',
    mascot: { name: 'Wilbur', text: 'is a carrier pigeon who takes an agent\u2019s question to your phone and flies the answer back. He is named for Wilbur Wright.' },
    name: 'wingman',
    // No href: the repo is private, so the card says so instead of linking.
    image: wingman,
    blurb:
      'When an agent needs approval, Wingman sends the question to my phone in plain language. I can answer without going back to my desk.',
    meta: { Type: 'Service + app', Language: 'Dart / Python' },
    tags: ['relay', 'mobile', 'hooks'],
    width: 214,
    offset: 34,
    lap: 100,
    z: 5,
    rot: 5,
  },
  {
    id: 'rvu',
    mascot: { name: 'Rufus', text: 'is Janus, the two-faced god of doorways. One face looks back at your past sessions and the other forward to the one you pick up.' },
    name: 'rvu',
    href: '/projects/rvu/',
    image: rvu,
    blurb:
      'I used to reconstruct old sessions from memory. rvu lists them, shows what happened in each, and lets me resume one.',
    meta: { Type: 'CLI', Language: 'TypeScript' },
    tags: ['sessions', 'context', 'npm'],
    width: 228,
    offset: -22,
    lap: 56,
    z: 7,
    rot: -3,
  },
  {
    id: 'docrot',
    mascot: { name: 'Digby', text: 'is a beagle who sniffs out docs that have gone stale.' },
    name: 'docrot',
    // No href: the repo is private, so the card says so instead of linking.
    image: docrot,
    blurb:
      'I did not want to update docs by hand, especially when stale ones look current. docrot scores their drift so agents can fix the ones it flags.',
    meta: { Type: 'CI tool', Language: 'Python' },
    tags: ['ci', 'docs', 'drift'],
    // In front of its neighbours, whose horn and corner cut into its face.
    // Still under a focused or opened icon, which sit at 9.
    width: 188,
    offset: -34,
    lap: 40,
    z: 8,
    rot: -5,
  },
  {
    id: 'nfctl',
    mascot: { name: 'Nanook', text: 'is a polar bear, after Numaflow\u2019s own mascot, and is named for the Inuit spirit of the polar bear.' },
    name: 'nfctl',
    href: 'https://github.com/txmxthy/nfctl',
    image: nfctl,
    blurb:
      'kubectl can create Numaflow pipelines but cannot follow a vertex\u2019s logs through pod churn or drain one safely. nfctl does both, draws the DAG, and puts buffer fill and rates beside the pipeline phase, in one static binary.',
    meta: { Type: 'CLI + TUI', Language: 'Rust', Status: 'Pre-release' },
    tags: ['numaflow', 'kubernetes', 'tui'],
    width: 204,
    offset: 26,
    lap: 66,
    z: 7,
    rot: 6,
  },
  {
    id: 'orthodag',
    mascot: { name: 'Otto', text: 'is an octopus whose tentacles route out like the edges orthodag draws.' },
    name: 'orthodag',
    href: 'https://github.com/txmxthy/orthodag',
    image: orthodag,
    blurb:
      'A terminal cannot show a Graphviz image, so pipelines drawn as text lose their edges in the crossings. orthodag lays a directed graph out as Unicode boxes and orthogonal edges that fit a target width, with no dependencies.',
    meta: { Type: 'Library', Language: 'Rust', Status: 'Pre-release' },
    tags: ['graphs', 'layout', 'unicode'],
    width: 222,
    offset: -18,
    lap: 62,
    z: 8,
    rot: -4,
  },
  {
    id: 'totem',
    mascot: { name: 'Trio', text: 'is three faces stacked into a pole. totem made every mascot in this row, Trio included.' },
    name: 'totem',
    // No href: the repo is private, so the card says so instead of linking.
    image: totem,
    blurb:
      'totem makes a family of project mascots with one hue each. It cuts, recolours and checks every icon, and rigs them into the animated SVGs on this page.',
    meta: { Type: 'CLI', Language: 'Python' },
    tags: ['mascots', 'svg', 'animation'],
    width: 250,
    offset: 14,
    lap: 128,
    z: 8,
    rot: 4,
  },
  {
    id: 'oasis',
    mascot: { name: 'Opal', text: 'is the palm at the oasis, in the teal of the water beneath it.' },
    name: 'oasis',
    href: '/projects/oasis/',
    image: oasis,
    blurb:
      'I wanted to ask questions of my documents without sending them anywhere. Oasis embeds PDFs, text and CSVs in a local Chroma database and answers offline.',
    meta: { Type: 'CLI', Language: 'Python' },
    tags: ['llm', 'rag', 'local'],
    width: 196,
    offset: 36,
    lap: 118,
    z: 6,
    rot: 7,
  },
  {
    id: 'hyperbench',
    mascot: { name: 'Hex', text: 'is a honeybee. A hive is a scheduling problem, with every worker\u2019s job planned across the flowers, and she is named for the honeycomb.' },
    name: 'hyperbench',
    href: '/projects/hyperbench/',
    image: hyperbench,
    blurb:
      'HyperBench runs exact solvers, metaheuristics and evolved dispatching rules on the same job-shop instances across a SLURM cluster, then compares their schedules.',
    meta: { Type: 'Benchmark harness', Language: 'Python + SLURM' },
    tags: ['scheduling', 'hyper-heuristics', 'hpc'],
    width: 184,
    offset: -26,
    lap: 70,
    z: 7,
    rot: -6,
  },
];
